import { Grid } from '@mui/material'
import { Form, Formik } from 'formik'
import { memo, useRef, useCallback } from 'react'
import { isEqual } from 'lodash'
import dayjs from 'dayjs'
import * as Yup from 'yup'
import { setError, setSuccess } from '../../../components/alerts'
import { metodoPrograma } from '../../../components/api'
import { useModal } from '../../../components/Utils/UseModal'
import ModalImprime from './modalImprime'
import PacienteCard from './components/PacienteCard'
import PropostaCard from './components/PropostaCard'
import { TerapiasPagasCard, ProdutosPagosCard } from './components/PagosCard'
import PedidosAbertoCard from './components/PedidosAbertoCard'
import VendaCheckoutCard from './components/VendaCheckoutCard'
import ResumoFinal from './components/ResumoFinal'
import { CheckoutProvider, useCheckout } from './components/CheckoutContext'

const buildValidationSchema = (totalGeral) =>
  Yup.object().shape({
    acFormaPagamento:
      totalGeral > 0
        ? Yup.array()
            .min(1, 'Selecione a forma de pagamento')
            .required('Campo obrigatório')
        : Yup.array().notRequired(),
    formasPagamento:
      totalGeral > 0
        ? Yup.array().of(
            Yup.object().shape({
              vlPago: Yup.number()
                .moreThan(0, 'O valor deve ser maior que 0')
                .required('valor é obrigatório')
                .typeError('valor deve ser um número'),
              qtParcelas: Yup.number().when('isParcelas', {
                is: true,
                then: (schema) =>
                  schema
                    .moreThan(
                      0,
                      'A quantidade de parcelas deve ser maior que 0',
                    )
                    .required('Quantidade de parcelas é obrigatória')
                    .typeError('Quantidade de parcelas deve ser um número'),
                otherwise: (schema) => schema.notRequired(),
              }),
            }),
          )
        : Yup.array().notRequired(),
  })

const trataItensImpressao = (itens = []) =>
  itens.map((item) => ({
    nome: item.dsProduto,
    paciente: item.nmPaciente || '',
    qtd: item.qtProduto || 0,
    unitario: item.vlUnitario || 0,
    valor: item.vlTotal || 0,
    snPago: item.snPago || 'N',
  }))

const EdicaoInner = ({ voltar }) => {
  const formikRef = useRef()

  const { initialValues, dependentes, listaProdutosAdicionais, getCalculos } =
    useCheckout()

  const modalImprime = useModal(ModalImprime)

  const imprimir = useCallback(
    async (values, setSubmitting) => {
      setSubmitting(true)

      const { valorTotalTabelaBase, totalDevedor } = getCalculos(values)
      const itensImpressao = values.idAtendimento
        ? await metodoPrograma(
            'getItensCheckoutImpressao',
            { idAtendimento: values.idAtendimento },
            'CtrlCheckout',
          )
        : null
      const resumoEstornos = values.idAtendimento
        ? await metodoPrograma(
            'resumoEstornos',
            { idAtendimento: values.idAtendimento },
            'CtrlCheckout',
          )
        : null
      const totalEstornado = resumoEstornos?.status
        ? Number(resumoEstornos.totalEstornado || 0)
        : 0

      await modalImprime({
        proposta: values.idAtendimento ? String(values.idAtendimento) : '',
        contratante: values.dsPaciente || '',
        suite: '',
        checkIn: values.dtInicio
          ? dayjs(values.dtInicio).format('DD/MM/YYYY')
          : '',
        dependentes,
        terapias: trataItensImpressao(itensImpressao?.listaTerapias),
        suplementos: trataItensImpressao(itensImpressao?.listaProdutos),
        produtosAdicionais: [],
        subTotal: valorTotalTabelaBase || 0,
        desconto: values.vlDesconto || 0,
        caucao: values.vlCaucao || 0,
        formasPagamento: (values?.acFormaPagamento || []).map((item, i) => ({
          nome: item?.[1] || '',
          vlPago: values?.formasPagamento?.[i]?.vlPago ?? item?.[2] ?? 0,
        })),
        totalEstornado,
        totalGeral: totalDevedor || 0,
        emissaoData: dayjs().format('DD [de] MMMM [de] YYYY'),
      })
      setSubmitting(false)
    },
    [modalImprime, dependentes, getCalculos],
  )

  const inserePedidoVenda = useCallback(
    async (values, { setSubmitting }) => {
      const { totalGeral, totalDevedor } = getCalculos(values)

      if (Math.abs(totalDevedor) >= 0.01) {
        setSubmitting(false)
        return setError('O valor pago precisa ser igual ao Total a Pagar')
      }

      const payload = {
        ...values,
        formasPagamento: values?.formasPagamento,
        itens: listaProdutosAdicionais.filter((item) => item?.qtProduto > 0),
        vlTotal: totalGeral,
      }

      const res = await metodoPrograma(
        'fechaAtendimento',
        payload,
        'CtrlCheckout',
      )
      if (res.status) {
        setSuccess('Pedido Registrado')
        return voltar()
      } else {
        setSubmitting(false)
        setError(res?.dsErro || 'Erro ao salvar!')
      }
    },
    [getCalculos, listaProdutosAdicionais, voltar],
  )

  const salvarRascunho = useCallback(
    async (values, setSubmitting) => {
      setSubmitting(true)
      const { totalGeral } = getCalculos(values)
      const res = await metodoPrograma(
        'salvaRascunho',
        {
          ...values,
          formasPagamento: values?.formasPagamento,
          itens: listaProdutosAdicionais.filter((item) => item?.qtProduto > 0),
          vlTotal: totalGeral,
        },
        'CtrlCheckout',
      )
      if (res.status) {
        setSuccess('Rascunho salvo')
        return voltar()
      }
      setSubmitting(false)
      setError(res?.dsErro || 'Erro ao salvar o rascunho!')
    },
    [getCalculos, listaProdutosAdicionais, voltar],
  )

  return (
    <Formik
      enableReinitialize
      validationSchema={buildValidationSchema(
        getCalculos(formikRef.current?.values || initialValues).totalGeral,
      )}
      initialValues={initialValues}
      innerRef={formikRef}
      onSubmit={inserePedidoVenda}
    >
      {() => (
        <Form
          style={{
            width: 'calc(100% + 20px)',
            height: 'calc(100vh - 140px)',
            display: 'flex',
            flexDirection: 'column',
            margin: '-10px',
          }}
        >
          <Grid
            container
            direction="column"
            wrap="nowrap"
            sx={{
              flex: 1,
              overflow: 'auto',
              bgcolor: 'grey.100',
              p: 2,
              gap: 2,
            }}
          >
            <Grid item>
              <PacienteCard />
            </Grid>

            <Grid item>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3} sx={{ display: 'flex' }}>
                  <PropostaCard preencherAltura />
                </Grid>
                <Grid item xs={12} md={3} sx={{ display: 'flex' }}>
                  <TerapiasPagasCard preencherAltura />
                </Grid>
                <Grid item xs={12} md={3} sx={{ display: 'flex' }}>
                  <ProdutosPagosCard preencherAltura />
                </Grid>
                <Grid item xs={12} md={3} sx={{ display: 'flex' }}>
                  <PedidosAbertoCard preencherAltura />
                </Grid>
              </Grid>
            </Grid>

            <Grid item>
              <Grid container spacing={2}>
                <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
                  <VendaCheckoutCard />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Grid container spacing={2} direction="column">
                    <Grid item>
                      <ResumoFinal
                        onImprimir={imprimir}
                        onSalvarRascunho={salvarRascunho}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}

const Edicao = ({ dados: { linha }, voltar, lojaPontoVenda, isModal }) => (
  <CheckoutProvider
    linha={linha}
    lojaPontoVenda={lojaPontoVenda}
    isModal={isModal}
  >
    <EdicaoInner voltar={voltar} />
  </CheckoutProvider>
)

export default memo(Edicao, isEqual)
